import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, exhaustMap, map, switchMap } from 'rxjs/operators';

import { ToastService } from '../../../modules/shared/services/toast-message.service';
import { PlanService } from '../../../services/plan/plan.service';
import { UserMeService } from '../../../services/user/user.service';
import { EApiErrorCode, IApiError } from '../../models/api-error';
import { IUser } from './models/UserMe';
import * as actions from './user.actions';

@Injectable()
export class UserMeEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly service: UserMeService,
    private readonly planService: PlanService,
    private readonly toastService: ToastService,
  ) {}

  getUserMe$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.getUserMe),
      exhaustMap(() =>
        this.service.getUserMe().pipe(
          map((userMe: IUser) => {
            return actions.getUserMeSuccess({ userMe });
          }),
          catchError((error: any) => of(actions.getUserMeFailure(error))),
        ),
      ),
    ),
  );

  // exhaustMap: a double click sends one request; the API's one-open-request rule is the
  // backstop for two tabs. A 409 is the API saying the request already exists, which is
  // its own outcome rather than a failure — the interceptor has already toasted it.
  requestPlanUpgrade$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.requestPlanUpgrade),
      exhaustMap(() =>
        this.planService.requestUpgrade().pipe(
          map(() => {
            this.toastService.success(
              'Your upgrade request has been sent. We will be in touch shortly.',
            );
            return actions.requestPlanUpgradeSuccess();
          }),
          catchError((error: unknown) => {
            const apiError: IApiError | null =
              error instanceof HttpErrorResponse ? error.error : null;
            if (
              apiError?.errorCode === EApiErrorCode.PLAN_CHANGE_ALREADY_REQUESTED
            ) {
              return of(
                actions.requestPlanUpgradeAlreadyRequested({
                  ticketNumber: apiError.ticketNumber ?? null,
                }),
              );
            }
            return of(actions.requestPlanUpgradeFailure({ error }));
          }),
        ),
      ),
    ),
  );

  // Either way a request is now open, and /user/me is where the page reads that from.
  reloadUserAfterPlanUpgradeRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        actions.requestPlanUpgradeSuccess,
        actions.requestPlanUpgradeAlreadyRequested,
      ),
      map(() => actions.getUserMe()),
    ),
  );

  getUserAll$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.getUserAll),
      exhaustMap(() =>
        this.service.getUserAll().pipe(
          map((allUsers: IUser[]) => {
            return actions.getUserAllSuccess({ allUsers });
          }),
          catchError((error: any) => of(actions.getUserAllFailure(error))),
        ),
      ),
    ),
  );

  setUserPreferences$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.setUserPreferences),
      exhaustMap((action) =>
        this.service.setUserPreferences(action).pipe(
          map(() => {
            return actions.setUserPreferencesSuccess({
              navigationToggled: action.navigationToggled,
              dashboardMyTasksToggled: action.dashboardMyTasksToggled,
            });
          }),
          catchError((error: any) =>
            of(actions.setUserPreferencesFailure(error)),
          ),
        ),
      ),
    ),
  );

  getUserNotifications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.getUserNotifications), // Listen for the action
      switchMap(({ filter }) =>
        this.service.getUserNotifications(filter).pipe(
          map(
            (notifications) =>
              actions.getUserNotificationsSuccess({
                filteredNotifications: notifications,
              }), // Dispatch success action
          ),
          catchError(
            (error) => of(actions.getUserNotificationsFailure({ error })), // Dispatch failure action
          ),
        ),
      ),
    ),
  );

  updateIsReadNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.updateIsReadNotification),
      exhaustMap((action) =>
        this.service.updateIsReadNotification(action.notificationId).pipe(
          map(() => {
            return actions.updateIsReadNotificationSuccess({
              notificationId: action.notificationId,
            });
          }),
          catchError((error: any) =>
            of(actions.updateIsReadNotificationFailure(error)),
          ),
        ),
      ),
    ),
  );

  markAsReadAllNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.markAsReadAllNotification),
      exhaustMap(() =>
        this.service.markAsReadAllNotifications().pipe(
          switchMap(() =>
            this.service.getUserNotifications({}).pipe(
              map((notifications) =>
                actions.getUserNotificationsSuccess({
                  filteredNotifications: notifications,
                }),
              ),
              catchError((error) =>
                of(actions.getUserNotificationsFailure({ error })),
              ),
            ),
          ),
          catchError((error: any) =>
            of(actions.markAsReadAllNotificationFailure({ error })),
          ),
        ),
      ),
    ),
  );
}
